import { useState } from 'react';
import { toast } from 'sonner';
import { EyeOff, Dices, Eye, RotateCcw, Loader2, GraduationCap } from 'lucide-react';
import { randomLeak, measIds, hiddenMeas, grade } from '../lib/aquaTrain';
import { measCfg } from '../lib/aquaLeakFind';
import { useAqua, setAqua } from '../lib/aquaStore';
import { hilite } from './CorrCalc';

const nf = (v, d = 2) => (v == null || !Number.isFinite(+v) ? '–' : (+v).toFixed(d).replace('.', ','));
const rerun = () => setTimeout(() => window.dispatchEvent(new Event('giss-aqua-rerun')), 60);

function Verdict({ t, g }) {
  if (!g) return <p className="muted" data-testid="trn-nograde">{t.trnNoSearch}</p>;
  const tips = [g.flat && t.lfFlat, !g.discr && t.trnTipNoP, g.discr && g.nH < 4 && t.trnTipFewP, g.level !== 'hit' && t.trnTipPlan, g.level !== 'hit' && t.trnTipNight, g.qTrue < 0.5 && t.trnTipSmall].filter(Boolean);
  return (
    <div className={`trn-verdict trn-${g.level}`} data-testid="trn-verdict">
      <b data-testid="trn-level">{t[`trnLv_${g.level}`]}</b>
      {g.per.map((x, i) => (
        <small key={x.id} data-testid={`trn-per-${i}`}>
          {x.name}: {x.rank ? t.trnRank.replace('{r}', x.rank).replace('{p}', nf(x.prob * 100, 1)) : t.trnNotInTop} · {t.trnDist} {nf(x.near, 0)} m · {t.trnQ} {nf(x.q)} l/s
        </small>
      ))}
      <small data-testid="trn-qcmp">{t.trnQcmp.replace('{e}', nf(g.qEst)).replace('{w}', nf(g.qTrue))}</small>
      {tips.length > 0 && <ul className="trn-tips">{tips.map((x) => <li key={x}>{x}</li>)}</ul>}
    </div>
  );
}

// training: hide own or random leak -> synthetic measurements -> leak search without knowing it -> reveal and grade
export function AquaTrain({ t, project, updateProject, cfg }) {
  const s = useAqua(), tr = cfg.train, [busy, setBusy] = useState(false), [noise, setNoise] = useState(true);
  const own = (project.fittings || []).filter((f) => f.type === 'leak' && f.hyd?.leakOn && f.hostPipe);
  const hide = async (random) => {
    const leaks = random ? [randomLeak(project, t.trnSecretName)].filter(Boolean) : own;
    if (!leaks.length) { toast.error(random ? t.aqLeakNoPipe : t.trnNoLeak); return; }
    setBusy(true);
    const ls = cfg.ls || {}, m = await hiddenMeas(project, measCfg(cfg), leaks, measIds(project, ls), noise);
    setBusy(false);
    if (m.error) { toast.error(t.aqHydCalcErr); return; }
    const ids = new Set(leaks.map((l) => l.id));
    updateProject((p) => ({ ...p, fittings: (p.fittings || []).filter((f) => !ids.has(f.id)), aqua: { ...(p.aqua || {}), ls: { ...ls, src: m.src, hyd: m.hyd, log: {} }, train: { leaks, q: m.q, random, noise, at: Date.now() } } }));
    setAqua({ ls: null });
    hilite(null);
    toast.success(t.trnHiddenOk);
    rerun();
  };
  const restore = (extra) => updateProject((p) => ({ ...p, fittings: [...(p.fittings || []), ...tr.leaks.filter((l) => !(p.fittings || []).some((f) => f.id === l.id))], aqua: { ...(p.aqua || {}), train: extra ? { ...tr, ...extra } : undefined } }));
  const reveal = () => {
    const g = grade(s.ls, tr);
    restore({ done: true, g });
    const top = s.ls?.top?.[0];
    hilite({ pins: tr.leaks.map((l) => [l.lat, l.lon]), lines: top ? [top.coords] : [] });
    window.__gissMapView?.()?.m?.setView([tr.leaks[0].lat, tr.leaks[0].lon], 18);
    rerun();
  };
  const reset = () => { hilite(null); const rid = tr?.random ? new Set(tr.leaks.map((l) => l.id)) : null; updateProject((p) => ({ ...p, fittings: rid ? (p.fittings || []).filter((f) => !rid.has(f.id)) : p.fittings, aqua: { ...(p.aqua || {}), train: undefined } })); rerun(); };
  const hidden = tr && !tr.done;
  return (
    <div className="aq-train" data-testid="trn-panel">
      <h4><GraduationCap size={16} /> {t.trnTitle}</h4>
      {!tr && <>
        <ol className="trn-steps muted"><li>{t.trnS1}</li><li>{t.trnS2}</li><li>{t.trnS3}</li><li>{t.trnS4}</li></ol>
        <label className="aq-inline"><input type="checkbox" data-testid="trn-noise" checked={noise} onChange={(e) => setNoise(e.target.checked)} /> {t.trnNoise}</label>
        <div className="row">
          <button className="btn btn-dark btn-sm" data-testid="trn-hide-own" disabled={busy || !own.length} onClick={() => hide(false)}>{busy ? <Loader2 size={14} className="spin" /> : <EyeOff size={14} />} {t.trnHideOwn} ({own.length})</button>
          <button className="btn btn-accent btn-sm" data-testid="trn-hide-random" disabled={busy} onClick={() => hide(true)}><Dices size={14} /> {t.trnHideRandom}</button>
        </div>
      </>}
      {hidden && <>
        <p className="trn-state" data-testid="trn-hidden"><EyeOff size={14} /> {(tr.random ? t.trnHiddenRandom : t.trnHiddenOwn).replace('{n}', tr.leaks.length)} · {t.trnMeasN.replace('{n}', Object.keys(cfg.ls?.hyd || {}).length)}{tr.noise ? ` · ${t.trnWithNoise}` : ''}</p>
        <p className="muted">{s.ls ? t.trnNowReveal : t.trnNowSearch}</p>
        <div className="row">
          <button className="btn btn-red btn-sm" data-testid="trn-reveal" onClick={reveal}><Eye size={14} /> {t.trnReveal}</button>
          <button className="btn btn-outline btn-sm" data-testid="trn-cancel" onClick={() => { restore(null); rerun(); }}><RotateCcw size={14} /> {t.trnCancel}</button>
        </div>
      </>}
      {tr?.done && <>
        <Verdict t={t} g={tr.g} />
        <button className="btn btn-outline btn-sm" data-testid="trn-again" onClick={reset}><RotateCcw size={14} /> {t.trnAgain}</button>
      </>}
    </div>
  );
}
